// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// DOM + state wiring for the operating-context cards on the dashboard.
// The pure helpers (card HTML, event row formatting, window builders)
// live in operating-context-cards.js — this factory adds the helpers
// closure and the click/selection state management.

import {
  operatingContextCards,
  operatingContextCardHtml,
  operatingContextReviewCardHtml,
  operatingContextCompanionSeries,
  operatingContextWindows as operatingContextWindowsPure,
  mixtureContextCardForWindow,
} from "./operating-context-cards.js";

export function createOperatingContextController({
  escapeHtml,
  formatCompactElapsedTime,
  detectionWindow,
  compactCylinderLabel,
  compactMissingHealthNote,
  reviewMonitorEnabled,
  availableSeriesKeys,
  // Engine descriptors of the shown flight: a twin's Mixture window selects the
  // traces of the engine it belongs to (its own fuel flow, RPM, MAP, cylinders).
  currentEngineDescriptors = () => [],
  activeSeries,
  syncSeriesControls,
  renderHealthSummary,
  updateHealthCardSelection,
  scheduleDrawAll,
  getHealthSummary,
  getSelectedOperatingContextKey,
  setSelectedOperatingContextKey,
  setSelectedOperatingContextWindows,
  setSelectedHealthKey,
  setOverviewSelectionCleared = () => {},
  setSelectedDetectionWindowCache,
  getDivergenceTrendRows = () => [],
  getSelectedFlightId = () => null,
  reviewNoteSectionHtml = null,
}) {
  // The Mixture card lists every resolver window in its drill-down; this is the
  // key of the window the review card and chart highlight currently describe.
  // null means the primary window. Selecting the card afresh resets it, and a
  // key that no longer exists (re-analysis, another flight) falls back to the
  // primary window.
  let selectedMixtureWindowKey = null;
  let renderedCards = [];
  let mixtureWindowInteractionBound = false;

  // The card as the review card should show it: the Mixture card re-pointed at
  // the selected window, every other card unchanged.
  function shownCard(card) {
    return mixtureContextCardForWindow(card, selectedMixtureWindowKey);
  }

  function commonHelpers() {
    return {
      escapeHtml,
      formatCompactElapsedTime,
      detectionWindow,
      compactCylinderLabel,
      compactMissingHealthNote,
      reviewMonitorEnabled: (key) => {
        if (reviewMonitorEnabled && !reviewMonitorEnabled(key)) return false;
        const available = availableSeriesKeys();
        if (key === "mag") return available.some((series) => /^(?:rpm|rpm\d+)$/.test(series));
        if (key === "charging") return available.includes("buss");
        return true;
      },
      selectedOperatingContextKey: getSelectedOperatingContextKey(),
      divergenceTrendRows: getDivergenceTrendRows(),
      currentFlightId: getSelectedFlightId(),
      reviewNoteSectionHtml,
    };
  }

  function operatingContextWindows(card) {
    return operatingContextWindowsPure(card, commonHelpers());
  }

  function selectedOperatingContextReviewCardHtml(summary) {
    const selectedKey = getSelectedOperatingContextKey();
    if (!selectedKey) return "";
    const helpers = commonHelpers();
    const card = operatingContextCards(summary, helpers).find((item) => item.key === selectedKey);
    return card ? operatingContextReviewCardHtml(shownCard(card), helpers) : "";
  }

  function selectCompanionSeriesForOperatingContext(key, event = null) {
    const available = new Set(availableSeriesKeys());
    const thermalKeys = [...available].filter((seriesKey) => /^(?:egt|cht)\d+$/i.test(seriesKey)).slice(0, 6);
    // The Mixture card carries one of the two persisted mixture metric keys; its
    // window event names the engine on a twin, so the event goes through.
    const eventScopedKeys = ["fuelPressure", "propellerRpm", "mixtureContext", "ropLeaningContext", "lopLeaningContext"];
    const companions = eventScopedKeys.includes(key) ? operatingContextCompanionSeries(key, [...available], event, currentEngineDescriptors()) : {
      mag: ["rpm", "fuelFlow", "map", ...thermalKeys],
      cylinderHarmony: ["fuelFlow", "rpm", "map", ...thermalKeys],
      chtRhythm: ["fuelFlow", "rpm", "map", "percentPower", ...thermalKeys],
      fuelFlowResponse: ["fuelFlow", "rpm", "map", "percentPower", "fuelPressure", ...thermalKeys.slice(0, 4)],
    }[key] ?? [];
    let changed = false;
    companions.forEach((seriesKey) => {
      if (!available.has(seriesKey) || activeSeries.has(seriesKey)) return;
      activeSeries.add(seriesKey);
      changed = true;
    });
    if (changed) syncSeriesControls();
  }

  function updateOperatingContextCardSelection() {
    const selectedKey = getSelectedOperatingContextKey();
    document.querySelectorAll("[data-operating-context-card]").forEach((card) => {
      const selected = selectedKey === card.dataset.operatingContextCard;
      card.classList.toggle("is-selected", selected);
      card.setAttribute("aria-pressed", selected ? "true" : "false");
    });
  }

  function rerenderAfterSelection() {
    const healthSummary = getHealthSummary();
    if (healthSummary) {
      renderHealthSummary(healthSummary);
    } else {
      updateHealthCardSelection();
      updateOperatingContextCardSelection();
    }
    scheduleDrawAll();
  }

  // Point the Mixture review card and the chart highlight at one of its windows,
  // exactly as selecting the card does for the primary window: the window's own
  // span, engine and companion series. Unknown keys are ignored.
  function selectMixtureWindow(windowKey) {
    const card = renderedCards.find((item) => item.key === "mixtureContext");
    if (!card || !(Array.isArray(card.windows) && card.windows.some((window) => window?.key === windowKey))) return false;
    selectedMixtureWindowKey = windowKey;
    const shown = shownCard(card);
    setOverviewSelectionCleared(false);
    setSelectedOperatingContextKey(card.key);
    setSelectedOperatingContextWindows(operatingContextWindowsPure(shown, commonHelpers()));
    setSelectedHealthKey(null);
    setSelectedDetectionWindowCache({ summary: null, key: "", windows: [] });
    selectCompanionSeriesForOperatingContext(shown.metricKey, shown.event);
    rerenderAfterSelection();
    return true;
  }

  // The review card is re-rendered by the health summary renderer (or the host),
  // so the window buttons are handled by one delegated listener that survives
  // every rebuild. Keyboard activation is the button's own (Enter/Space); focus
  // is handed back to the re-rendered button so a keyboard user stays in place.
  function bindMixtureWindowInteractions() {
    if (mixtureWindowInteractionBound || typeof document?.addEventListener !== "function") return;
    mixtureWindowInteractionBound = true;
    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const option = target.closest("[data-mixture-window]");
      if (!option || option.getAttribute("aria-pressed") === "true") return;
      const windowKey = option.getAttribute("data-mixture-window");
      const hadFocus = document.activeElement === option;
      if (!selectMixtureWindow(windowKey)) return;
      if (!hadFocus) return;
      const rendered = document.querySelector(`[data-mixture-window="${CSS.escape(String(windowKey))}"]`);
      if (rendered && rendered !== option && typeof rendered.focus === "function") rendered.focus({ preventScroll: true });
    });
  }

  function renderOperatingContextCards(summary) {
    const container = document.querySelector("#operatingContextCards");
    const grid = document.querySelector("#operatingContextCardGrid");
    if (!container || !grid) return;
    const helpers = commonHelpers();
    const cards = operatingContextCards(summary, helpers);
    renderedCards = cards;
    if (!cards.length) {
      container.hidden = true;
      grid.innerHTML = "";
      setSelectedOperatingContextKey(null);
      setSelectedOperatingContextWindows([]);
      selectedMixtureWindowKey = null;
      return;
    }
    container.hidden = false;
    let selectedKey = getSelectedOperatingContextKey();
    if (selectedKey && !cards.some((card) => card.key === selectedKey)) {
      setSelectedOperatingContextKey(null);
      setSelectedOperatingContextWindows([]);
      selectedKey = null;
    }
    const selectedCard = cards.find((card) => card.key === selectedKey);
    if (selectedMixtureWindowKey && !(selectedCard?.key === "mixtureContext"
      && Array.isArray(selectedCard.windows) && selectedCard.windows.some((window) => window?.key === selectedMixtureWindowKey))) {
      // The remembered window belongs to another flight or an older analysis.
      selectedMixtureWindowKey = null;
    }
    if (selectedCard) setSelectedOperatingContextWindows(operatingContextWindowsPure(shownCard(selectedCard), helpers));
    grid.innerHTML = cards.map((card) => operatingContextCardHtml(card, helpers)).join("");
    grid.querySelectorAll("[data-operating-context-card]").forEach((button) => {
      button.addEventListener("click", () => {
        const card = cards.find((item) => item.key === button.dataset.operatingContextCard);
        if (!card) return;
        // Selecting the card afresh returns the Mixture review to its primary window.
        selectedMixtureWindowKey = null;
        setOverviewSelectionCleared(false);
        setSelectedOperatingContextKey(card.key);
        setSelectedOperatingContextWindows(operatingContextWindowsPure(card, commonHelpers()));
        setSelectedHealthKey(null);
        setSelectedDetectionWindowCache({ summary: null, key: "", windows: [] });
        if (["fuelPressure", "propellerRpm"].includes(card.metricKey)) {
          const traces = card.events.length ? card.events : card.coverage ?? [];
          traces.forEach((event) => selectCompanionSeriesForOperatingContext(card.metricKey, event));
        } else {
          selectCompanionSeriesForOperatingContext(card.metricKey, card.event);
        }
        rerenderAfterSelection();
      });
    });
    bindMixtureWindowInteractions();
    updateOperatingContextCardSelection();
  }

  return {
    renderOperatingContextCards,
    updateOperatingContextCardSelection,
    selectCompanionSeriesForOperatingContext,
    operatingContextWindows,
    selectedOperatingContextReviewCardHtml,
    selectMixtureWindow,
    getSelectedMixtureWindowKey: () => selectedMixtureWindowKey,
  };
}
